import React, { ReactElement, useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { SSRApiContext } from '@ecommerce/nest-ssr/common';

import { type TabsItemInterface, TabsItemType } from 'components/tabs/tabs.interface';
import Loading from 'atomic/loading/loading';
import AdSlot from 'components/ads/ad-slot';
import { SLOT_FLOAT_RIGHT } from 'components/ads/ad-slot/ad-defs';

import './tabs.scss';

interface TabsProps {
    items: TabsItemInterface[];
    active: number;
    tabsClassName?: string;
    contentsClassName?: string;
}

const Tabs: React.FC<TabsProps> = ({ items, active, tabsClassName, contentsClassName }): ReactElement => {
    const api = useContext(SSRApiContext);

    if (api === null) {
        throw new Error('Invalid API context');
    }

    items = items.filter(item => item.isVisible);

    const [activeTab, setActiveTab] = useState(active || 0);
    const [tabItems, setTabItems] = useState(items);

    const handleLoad = (index: number): void => {
        setTabItems(oldTabItems => {
            const newTabItems = [...oldTabItems];

            newTabItems[index].isLazy = false;

            return newTabItems;
        });
    };

    const tabs = tabItems.map((item: TabsItemInterface, index: number) => {
        const link = (
            <a
                className="tabs__item-link"
                href={item.url}
                title={item.name}
                onClick={(): void => {
                    if (item.type !== TabsItemType.static) {
                        setActiveTab(index);
                    }
                }}
            >
                {item.name}
            </a>
        );

        return (
            <li
                key={item.name}
                className={classNames({ 'tabs__item': true, 'tabs__item-active': activeTab === index })}
            >
                {
                    item.tabAsHeader
                        ? (
                            <h2 className="tabs__item-header">
                                {link}
                            </h2>
                        )
                        : link
                }
            </li>
        );
    });

    const tabContents = tabItems.map((item: TabsItemInterface, index: number) => {
        let ItemComponent;

        if (item.type === TabsItemType.static) {
            return null;
        }

        if (item.isLazy) {
            if (activeTab !== index) {
                return null;
            }

            ItemComponent = (
                <React.Suspense key={item.name} fallback={<Loading callback={(): void => handleLoad(index)} />}>
                    <item.component isVisible={activeTab === index} {...item.props} />
                </React.Suspense>
            );
        } else {
            ItemComponent = item.type === TabsItemType.advanced ? <item.component isVisible={activeTab === index} {...item.props} /> : item.component;
        }

        return (
            <div
                key={item.name}
                className={classNames({ 'tabs__contents-item': true, 'tabs__contents-item-active': activeTab === index })}
            >
                {ItemComponent}
            </div>
        );
    });

    const changeHash = (): void => {
        const index = tabItems.findIndex(item => item.url === window.location.hash);

        if (index !== -1 && index !== activeTab) {
            setActiveTab(index);
        }
    };

    useEffect(
        () => {
            changeHash();
            window.addEventListener('hashchange', changeHash);
        },
        [] // eslint-disable-line react-hooks/exhaustive-deps
    );

    return (
        <div className="tabs">
            <div className="tabs__items-container">
                <div className={`tabs__items ${tabsClassName || ''}`}>
                    <ul className="tabs__items-list">
                        {tabs}
                    </ul>
                </div>
            </div>
            <div className="tabs__contents-container">
                <div className={`tabs__contents-wrapper ${contentsClassName || ''}`}>
                    <div className="tabs__contents">
                        {tabContents}
                    </div>
                    {
                        api.getDevice() !== 'mobile' && (
                            <div className="tabs__slot-right">
                                <AdSlot name={SLOT_FLOAT_RIGHT} injectAdsenseOnEmpty={true} className="tabs__slot-right-box" />
                            </div>
                        )
                    }
                </div>
            </div>
        </div>
    );
};

export default Tabs;
